import { useCallback, useMemo, useRef, useState } from "react";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useLocation } from "react-router";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { ObraAreaChecklistHeader } from "../../domain/models/obra_area_checklist_header";
import { useObrasChecklistareaFilters } from "../hooks/Obras_checklistarea/filters";
import { useObrasChecklistareaPagination } from "../hooks/Obras_checklistarea/pagination";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { ObraChecklistServico } from "../../domain/models/obra_checklist_servico";
import { ObraChecklistServicoForm } from "../schemas/Obras_checklistarea/obra_checklist_servico";
import { ObraChecklistServicoLiberacao } from "../../domain/models/obra_checklist_servico_liberacao";
import { ObraChecklistServicoLiberacaoForm, ObraChecklistServicoLiberacaoSchema } from "../schemas/Obras_checklistarea/obra_checklist_servico_liberacao";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";

/**
 * LoadObraChecklistServicosState
 */

type LoadObraChecklistServicosState = {
  idObra?: number;
  idObraAreaChecklist?: number;
  statusChecklist?: number;
  statusVerificacao?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useObrasChecklistareaViewModel
 */

export const useObrasChecklistareaViewModel = () => {
  const location = useLocation();

  const {
    commons: { getFuncionariosDropDownUseCase },
    obras: {
      getChecklistServicosByIdObraDropDownUseCase,
      getObraHeaderInfoUseCase,
      getObraAreaChecklistHeaderUseCase,
      getAllObraChecklistServicoUseCase,
      createObraChecklistServicosUseCase,
      deleteObraChecklistServicosUseCase,
      getObraChecklistServicoLiberacaoByIdUseCase,
      saveObraChecklistServicoLiberacaoUseCase,
    },
  } = useDependencyInjection();

  const {
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    setPageSaving,
  } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idObra, setIdObra] = useState<number>();
  const [idObraAreaChecklist, setIdObraAreaChecklist] = useState<number>();

  const [obraAreaChecklistHeader, setObraAreaChecklistHeader] = useState<ObraAreaChecklistHeader>();

  const [obraChecklistServico, setObraChecklistServico] = useState<ObraChecklistServico[]>();

  const [selectedObraChecklistServico, setSelectedObraChecklistServico] = useState<ObraChecklistServico>();

  const [obraChecklistServicoModal, setObraChecklistServicoModal] = useState(false);
  const [obraChecklistServicoLiberacaoModal, setObraChecklistServicoLiberacaoModal] = useState(false);
  const [obraChecklistServicoDeleteModal, setObraChecklistServicoDeleteModal] = useState(false);
  const [obraChecklistServicoDeleteLoteModal, setObraChecklistServicoDeleteLoteModal] = useState(false);

  const [obraChecklistServicoLiberacao, setObraChecklistServicoLiberacao] = useState<ObraChecklistServicoLiberacao>();
  const [loadingLiberacao, setLoadingLiberacao] = useState(false);
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>([]);

  const liberacaoRequest = useRef(0);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra });
  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

  const filters = useObrasChecklistareaFilters();
  const pagination = useObrasChecklistareaPagination();

  const checklistServicoDescription = useMemo(() => {
    if (obraChecklistServico === undefined) return null;
    if (obraChecklistServicoLiberacao === undefined) return null;

    const item = obraChecklistServico.find(item =>
      item.idObraChecklistServico === obraChecklistServicoLiberacao?.idObraChecklistServico);

    if (item === undefined) return null;

    return item.revisao !== null
      ? `${item.codigo} - ${item.descricao}, Rev.: ${item.revisao}`
      : `${item.codigo} - ${item.descricao}`;
  }, [
    obraChecklistServico,
    obraChecklistServicoLiberacao,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadChecklistServicosDropDown = useCallback(async (idObraParam?: number, idObraAreaChecklistParam?: number, tipoServico?: number) => {
    try {
      const filteredIdObra = idObra ?? idObraParam;
      const filtrereIdObraAreaChecklist = idObraAreaChecklist ?? idObraAreaChecklistParam;
      const checklistServicoDropdown = await getChecklistServicosByIdObraDropDownUseCase.getChecklistServicosByIdObraDropDown(
        filteredIdObra, filtrereIdObraAreaChecklist, tipoServico);
      useObrasChecklistareaFilters.setState({ checklistServicoDropdown });
    } catch (e: unknown) {
      throw new Error("Falha ao buscar dropdown de checklists!");
    }
  }, [
    idObra,
    idObraAreaChecklist,
    getChecklistServicosByIdObraDropDownUseCase,
  ]);

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  const loadObraAreaChecklistHeader = useCallback(async (id?: number) => {
    try {
      const filteredIdObraAreaChecklist = id ?? idObraAreaChecklist;
      const response = await getObraAreaChecklistHeaderUseCase.getObraAreaChecklistHeader(filteredIdObraAreaChecklist);
      setObraAreaChecklistHeader(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da area!');
    }
  }, [
    idObraAreaChecklist,
    getObraAreaChecklistHeaderUseCase,
  ]);

  const loadObraChecklistServicos = useCallback(async (fs?: LoadObraChecklistServicosState) => {
    try {
      const filteredIdObra = fs?.idObra ?? idObra;
      const filteredIdObraAreaChecklist = fs?.idObraAreaChecklist ?? idObraAreaChecklist;
      const filteredStatusChecklist = evaluateParamValue(fs?.statusChecklist, filters.statusChecklist);
      const filteredStatusVerificacao = evaluateParamValue(fs?.statusVerificacao, filters.statusVerificacao);
      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllObraChecklistServicoUseCase.getAllObraChecklistServico(
        filteredIdObra,
        filteredIdObraAreaChecklist,
        filteredStatusChecklist,
        filteredStatusVerificacao,
        filteredPage,
        filteredRowsPerPage,
      );

      setObraChecklistServico(getAllObraChecklistServicoUseCase.items);
      pagination.setTotalItems(getAllObraChecklistServicoUseCase.totalItems);
    } catch (e: unknown) {
      setObraChecklistServico(undefined);
      throw new Error('Falha ao buscar dados da serviços!');
    }
  }, [
    idObra,
    idObraAreaChecklist,
    filters,
    pagination,
    getAllObraChecklistServicoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationObraId = params.get('id');
      const locationIdChecklistArea = params.get('idChecklistArea');
      const locIdObra = locationObraId ?? undefined;
      const locIdChecklistArea = locationIdChecklistArea ?? undefined;

      if (!locIdObra || locIdObra.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(Number(locIdObra));

      if (!locIdChecklistArea || locIdChecklistArea.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de area!");
        return;
      }
      setIdObraAreaChecklist(Number(locIdChecklistArea));

      await loadObraHeaderInfo(Number(locIdObra));

      await loadObrasCommonReportsData(Number(locIdObra));

      await loadChecklistServicosDropDown(Number(locIdObra), Number(locIdChecklistArea));

      await loadObraAreaChecklistHeader(Number(locIdChecklistArea));

      useObrasChecklistareaPagination.setState({ page: 1 });
      await loadObraChecklistServicos({ idObra: Number(locIdObra), idObraAreaChecklist: Number(locIdChecklistArea), page: 1 });
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados das paginas!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: `/Obras_cadastroarea?id=${idObra}` });
  }, [
    idObra,
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idObra && idObraAreaChecklist) {
      try {
        setLoading(true);

        await loadObraHeaderInfo(idObra);

        await loadObrasCommonReportsData(idObra);

        await loadChecklistServicosDropDown(idObra, idObraAreaChecklist);

        await loadObraAreaChecklistHeader(idObraAreaChecklist);

        await loadObraChecklistServicos({ idObra: idObra, idObraAreaChecklist: idObraAreaChecklist });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    idObraAreaChecklist,
    loadObraHeaderInfo,
    loadObrasCommonReportsData,
    loadChecklistServicosDropDown,
    loadObraAreaChecklistHeader,
    loadObraChecklistServicos,
    pushErrorNotification,
  ]);

  const handleChecklistServicoOpenState = useCallback((idObraChecklistServico: number | null, openState: boolean) => {
    if (openState) {
      if (idObraChecklistServico && obraChecklistServico) {
        setSelectedObraChecklistServico(obraChecklistServico.find(x => x.idObraChecklistServico === idObraChecklistServico));
      } else {
        setSelectedObraChecklistServico(undefined);
      }
    }
    setObraChecklistServicoModal(openState);
  }, [
    obraChecklistServico,
  ]);

  const handleSubmitChecklistServicos = useCallback(async (form: ObraChecklistServicoForm) => {
    if (idObra && idObraAreaChecklist) {
      try {
        setSaving(true);
        setPageSaving(true);
        await createObraChecklistServicosUseCase.createObraChecklistServicos(
          idObra, idObraAreaChecklist, form.checklistServicos);

        setObraChecklistServicoModal(false);

        await loadChecklistServicosDropDown();
        await loadObraChecklistServicos();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar checklist de serviço!"));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    idObra,
    idObraAreaChecklist,
    createObraChecklistServicosUseCase,
    loadChecklistServicosDropDown,
    loadObraChecklistServicos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleEnterInspecoes = useCallback((idObraChecklistServico: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/ObraItensChecklistArea_cadastro?id=${idObra}&idChecklistArea=${idObraAreaChecklist}&idChecklistServico=${idObraChecklistServico}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/ObraItensChecklistArea_cadastro?id=${idObra}&idChecklistArea=${idObraAreaChecklist}&idChecklistServico=${idObraChecklistServico}`, { alternativeRoute: `/Obras_checklistarea?id=${idObra}&idChecklistArea=${idObraAreaChecklist}` });
    }
  }, [
    idObra,
    idObraAreaChecklist,
    backOrNavigate,
  ]);

  const handleDeleteLoteObraChecklistServicoOpenState = useCallback((openState: boolean) => {
    if (openState) {
      if (!rowSelectionModel || !rowSelectionModel.ids.size) {
        pushWarnNotification("Alerta", "Você deve selecionar os elementos a serem excluidos!");
        return;
      }
    }
    setObraChecklistServicoDeleteLoteModal(openState);
  }, [
    rowSelectionModel,
    pushWarnNotification,
  ]);

  const handleDeleteLoteObraChecklistServicoConfirm = useCallback(async () => {
    if (rowSelectionModel && rowSelectionModel.ids.size > 0) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteObraChecklistServicosUseCase.deleteObraChecklistServicos(
          Array.from(rowSelectionModel.ids, x => {
            if (typeof x === 'number') {
              return x;
            }
            if (typeof x === 'string') {
              return Number(x);
            }
            throw new Error("Invalid selection model!");
          })
        );

        await loadObraChecklistServicos();

        setObraChecklistServicoDeleteLoteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir checklist de serviço!"));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    rowSelectionModel,
    deleteObraChecklistServicosUseCase,
    loadObraChecklistServicos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleDeleteObraChecklistServicoOpenState = useCallback((idObraChecklistObra: number | null, openState: boolean) => {
    if (openState) {
      if (idObraChecklistObra && obraChecklistServico) {
        setSelectedObraChecklistServico(obraChecklistServico.find(x => x.idObraChecklistServico === idObraChecklistObra));
      } else {
        setSelectedObraChecklistServico(undefined);
      }
    }
    setObraChecklistServicoDeleteModal(openState);
  }, [
    obraChecklistServico,
  ]);

  const handleDeleteObraChecklistServicoConfirm = useCallback(async () => {
    if (selectedObraChecklistServico) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteObraChecklistServicosUseCase.deleteObraChecklistServicos([selectedObraChecklistServico.idObraChecklistServico]);
        await loadObraChecklistServicos();

        setObraChecklistServicoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir checklist de serviço!"));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    selectedObraChecklistServico,
    deleteObraChecklistServicosUseCase,
    loadObraChecklistServicos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
    setRowSelectionModel(rows);
  }, [
  ]);

  const handleSelectTipoServico = useCallback(async (tipoServico: number) => {
    if (idObra && idObraAreaChecklist) {
      try {
        setLoading(true);
        await loadChecklistServicosDropDown(idObra, idObraAreaChecklist, tipoServico);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    idObraAreaChecklist,
    loadChecklistServicosDropDown,
    pushErrorNotification,
  ]);

  const handleStatusChecklistChange = useCallback(async (statusChecklist: number) => {
    try {
      setLoading(true);
      useObrasChecklistareaFilters.setState({ statusChecklist });
      useObrasChecklistareaPagination.setState({ page: 1 });
      await loadObraChecklistServicos({ page: 1, statusChecklist });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraChecklistServicos,
    pushErrorNotification,
  ]);

  const handleStatusVerificacaoChange = useCallback(async (statusVerificacao: number) => {
    try {
      setLoading(true);
      useObrasChecklistareaFilters.setState({ statusVerificacao });
      useObrasChecklistareaPagination.setState({ page: 1 });
      await loadObraChecklistServicos({ page: 1, statusVerificacao });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraChecklistServicos,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useObrasChecklistareaFilters.setState({ statusChecklist: 2, statusVerificacao: 3 });
      useObrasChecklistareaPagination.setState({ page: 1 });
      await loadObraChecklistServicos({ page: 1, statusChecklist: 2, statusVerificacao: 3 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraChecklistServicos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObrasChecklistareaPagination.setState({ page: page + 1 });
      await loadObraChecklistServicos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraChecklistServicos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObrasChecklistareaPagination.setState({ page: 1, rowsPerPage });
      await loadObraChecklistServicos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar checklist de serviços"));
    } finally {
      setLoading(false);
    }
  }, [
    loadObraChecklistServicos,
    pushErrorNotification,
  ]);

  const handleLiberarChecklistOpenState = useCallback(async (
    idObraChecklistServico: number | null,
    idChecklistServico: number | null,
    openState: boolean) => {
    if (saving) return;
    const request = ++liberacaoRequest.current;
    setObraChecklistServicoLiberacao(undefined);
    if (!openState) {
      setObraChecklistServicoLiberacaoModal(false);
      setLoadingLiberacao(false);
      return;
    }
    if (!idObraChecklistServico || !idChecklistServico) {
      pushWarnNotification('Alerta', 'Selecione um checklist de serviço válido!');
      return;
    }
    try {
      setLoadingLiberacao(true);
      setObraChecklistServicoLiberacaoModal(true);

      const [response, funcionarios] = await Promise.all([
        getObraChecklistServicoLiberacaoByIdUseCase.execute(idObraChecklistServico, idChecklistServico),
        getFuncionariosDropDownUseCase.getFuncionariosDropDown(undefined, true, false, 0),
      ]);

      if (request === liberacaoRequest.current) {
        setObraChecklistServicoLiberacao(response);
        setFuncionariosDropdown(funcionarios.filter(x => x.canBeSelected));
      }
    } catch (e: unknown) {
      if (request === liberacaoRequest.current) {
        setObraChecklistServicoLiberacaoModal(false);
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar liberação do checklist de serviço!'));
      }
    } finally {
      if (request === liberacaoRequest.current) setLoadingLiberacao(false);
    }
  }, [
    saving,
    getObraChecklistServicoLiberacaoByIdUseCase,
    getFuncionariosDropDownUseCase,
    pushWarnNotification,
    pushErrorNotification,
  ]);

  const handleSubmitChecklistServicosLiberacao = useCallback(async (form: ObraChecklistServicoLiberacaoForm) => {
    if (!obraChecklistServicoLiberacao || loadingLiberacao || saving) return;
    try {
      setSaving(true);
      setPageSaving(true);
      const dados = ObraChecklistServicoLiberacaoSchema.parse(form);
      await saveObraChecklistServicoLiberacaoUseCase.execute(
        obraChecklistServicoLiberacao.idObraChecklistServico,
        obraChecklistServicoLiberacao.idChecklistServico,
        dados,
      );
      setObraChecklistServicoLiberacaoModal(false);
      setObraChecklistServicoLiberacao(undefined);
      pushInfoNotification('Sucesso', 'Liberação salva com sucesso!');
      await loadObraChecklistServicos();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao salvar liberação do checklist de serviço!'));
    } finally {
      setPageSaving(false);
      setSaving(false);
    }
  }, [
    obraChecklistServicoLiberacao,
    loadingLiberacao,
    saving,
    saveObraChecklistServicoLiberacaoUseCase,
    loadObraChecklistServicos,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  return {
    loading,
    loadingLiberacao,
    funcionariosDropdown,
    saving,
    errorMessage,
    idObra,
    idObraAreaChecklist,
    obraAreaChecklistHeader: obraAreaChecklistHeader ?? null,
    obraChecklistServico: obraChecklistServico ?? [],
    selectedObraChecklistServico: selectedObraChecklistServico ?? null,
    obraChecklistServicoModal,
    obraChecklistServicoLiberacaoModal,
    obraChecklistServicoDeleteModal,
    obraChecklistServicoDeleteLoteModal,
    rowSelectionModel,
    obraChecklistServicoLiberacao: obraChecklistServicoLiberacao ?? null,
    obrasTopHeaderState,
    obrasReportState,
    selectedObraHeaderInfo,
    filters,
    pagination,
    checklistServicoDescription,
    handleBack,
    handleReload,
    handleChecklistServicoOpenState,
    handleSubmitChecklistServicos,
    handleEnterInspecoes,
    handleDeleteLoteObraChecklistServicoOpenState,
    handleDeleteLoteObraChecklistServicoConfirm,
    handleDeleteObraChecklistServicoOpenState,
    handleDeleteObraChecklistServicoConfirm,
    handleRowSelectionModelChange,
    handleSelectTipoServico,
    handleStatusChecklistChange,
    handleStatusVerificacaoChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleLiberarChecklistOpenState,
    handleSubmitChecklistServicosLiberacao,
  };
}
